import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
    Skeleton,
    useDisclosure,
    Text,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { reduce } from 'lodash';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';
import useItemCreationWizard from 'hooks/useItemCreationWizard';

import { createItem, getItemTypeGenericAssetTemplates, applyGenericAssetTemplateItemType } from '../../services/items';
import { validateLongLat } from '../../services/portfolios';
import { isSubmitDisabled } from '../../services/utils';
import { getAutoFormGenericAssetTemplates, applyGenericAssetTemplate } from '../../services/autoforms';
import {
    TEN_SECOND_DURATION_IN_MILLISECONDS,
    THIRTY_SECOND_DURATION_IN_MILLISECONDS,
    TWENTY_SECOND_DURATION_IN_MILLISECONDS,
} from 'constants/time';

import { ReactComponent as AddIcon } from 'icons/add-item.svg';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';
import CheckboxFormikField from '../forms/CheckboxFormikField';
import ErrorAccordion from './ErrorAccordion';
import SecondaryButton from '../utils/SecondaryButton';
import AddWeatherZoneModal from 'components/itemCreationWizard/CustomSubItemCreation/WeatherZone/AddWeatherZoneModal';

// Shared component by Loads/Batteries/Renewables for adding new item of the given type
const AddItemModal = ({
    itemTypeId,
    updateItems,
    typeLabel,
    extraFields,
    extraFieldsInitialValues = {},
    formLabelsPhrasing = {},
    starterDataAutoFormSchemaCode = '',
    hideStarterDataOption = false,
    ...rest
}) => {
    const intl = useIntl();
    const { toast } = useCommonToast();
    const historyPush = useHistoryPush();
    const books = useSelector((state) => state.item.dataForItemCreation.books);
    const book = useSelector((state) => state.connections.book);

    const dataForItemCreation = useSelector((state) => state.item.dataForItemCreation);
    const pricingPoints = dataForItemCreation?.pricingPoints;
    const rtelecBasis = dataForItemCreation?.rtelecBasis;

    const rescalingSupportedItemTypes = useSelector((state) => state.item.rescalingSupportedItemTypes);
    const itemTypesWithWeatherZones = useSelector((state) => state.item.itemTypesWithWeatherZones);
    const isRescalingSupported = rescalingSupportedItemTypes.some((item) => item.itemTypeId === itemTypeId);

    const [genericAssetTemplateFolder, setTemplateFolder] = useState([]);
    const [useStarterDataFlag, setUserStarterData] = useState(false);

    const createWeatherZoneModal = useDisclosure();

    const { availableWeatherZones, updateAvailableWeatherZones } = useItemCreationWizard();

    const displayGenericAssetWeatherZones = useMemo(() => {
        return itemTypesWithWeatherZones?.includes(itemTypeId) ?? false;
    }, [itemTypesWithWeatherZones, itemTypeId]);

    const formLabels = {
        itemName: intl.formatMessage({ id: 'common_item_name' }),
        book: intl.formatMessage({ id: 'common_book' }),
        pricingPoint: intl.formatMessage({ id: 'common_electric_pricing_point' }),
        rtelecBasis: intl.formatMessage({ id: 'common_rtelec_pricing_point' }),
        emptyPlaceholder: ' ',
        longitude: intl.formatMessage({ id: 'longitude' }),
        latitude: intl.formatMessage({ id: 'latitude' }),
        ...formLabelsPhrasing,
    };

    const initialFormValues = {
        itemName: '',
        bookId: book,
        pricingPointId: null,
        rtelecBasisPriceId: null,
        latitude: '',
        longitude: '',
        useStarterData: false,
        ...extraFieldsInitialValues,
    };

    const removeFileExtensionRegex = /\.[^/.]+$/;

    const validateItemName = (value) => {
        const trimmed = value.trim();

        if (!trimmed) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Item name' });
        }

        if (trimmed.length > 100) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: 'Item name', lengthRule: '100 characters or less' }
            );
        }
    };

    const isEmptyBook = (value) => {
        if (!value) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Book' });
        }
    };

    const validateScalingValue = (value) => {
        if (value !== undefined && value !== '') {
            if (value <= 0) {
                return intl.formatMessage({ id: 'rescaling_scalingValue_input_error' });
            }
        }
    };

    const onFormSubmit = async ({
        itemName,
        bookId,
        pricingPointId,
        rtelecBasisPriceId,
        longitude,
        latitude,
        selectedTemplate,
        useStarterData,
        weatherZoneId,
        ...values
    }) => {
        const successMessage = intl.formatMessage({ id: 'common_item_creation_success' }, { type: typeLabel });
        const arcGISError = intl.formatMessage({ id: 'common_item_arcgis_lat_long_save_failed' });

        const extraFieldsValues = reduce(
            values,
            (acc, value, key) => {
                acc[key] = value || null;
                return acc;
            },
            {}
        );

        const payload = {
            description: itemName,
            bookId,
            itemTypeId,
            pricingPointId: pricingPointId || null,
            rtelecBasisPriceId: rtelecBasisPriceId || null,
            longitude: longitude !== '' ? longitude : null,
            latitude: latitude !== '' ? latitude : null,
            ...extraFieldsValues,
        };

        const { data, arcGisIssue } = await createItem(payload);
        const newItemData = data;

        updateItems(newItemData);

        if (useStarterData && selectedTemplate) {
            //set timeout for applying template
            let timeoutHit = false;
            const timeout = new Promise((resolve) =>
                setTimeout(() => {
                    timeoutHit = true;
                    resolve();
                }, TEN_SECOND_DURATION_IN_MILLISECONDS)
            );

            //apply template but move on if it takes too long
            const applyTemplate = async () => {
                if (starterDataAutoFormSchemaCode) {
                    await applyGenericAssetTemplate(starterDataAutoFormSchemaCode, newItemData.id, selectedTemplate);
                } else {
                    const scalingValue = isRescalingSupported ? values.scalingValue : 1;
                    await applyGenericAssetTemplateItemType(
                        itemTypeId,
                        newItemData.id,
                        selectedTemplate,
                        displayGenericAssetWeatherZones ? weatherZoneId : 0,
                        scalingValue
                    );
                }
            };
            await Promise.race([applyTemplate(), timeout]).then(() => {
                //display success message if item fully created or refresh page message if timeout hit
                if (timeoutHit) {
                    const apiDidNotFinishMessage = intl.formatMessage({ id: 'generic_asset_item_created_message' });
                    toast({
                        status: 'info',
                        message: apiDidNotFinishMessage,
                        slot: arcGisIssue && (
                            <ErrorAccordion message={arcGISError} details={arcGisIssue} status={'warn'} />
                        ),
                        duration: THIRTY_SECOND_DURATION_IN_MILLISECONDS,
                    });
                } else {
                    toast({
                        message: successMessage,
                        slot: arcGisIssue && (
                            <ErrorAccordion message={arcGISError} details={arcGisIssue} status={'warn'} />
                        ),
                    });
                }

                //navigate to new item and close modal
                historyPush(`${newItemData.id}`);
                rest.onClose();
            });
        } else {
            historyPush(`${newItemData.id}`);
            rest.onClose();
            toast({
                message: successMessage,
                slot: arcGisIssue && <ErrorAccordion message={arcGISError} details={arcGisIssue} status={'warn'} />,
                duration: arcGisIssue && TWENTY_SECOND_DURATION_IN_MILLISECONDS,
            });
        }
    };

    const validate = (values) => {
        let longitude = validateLongLat(values.longitude, 'long', values.latitude);
        let latitude = validateLongLat(values.latitude, 'lat', values.longitude);

        return {
            ...(longitude ? { longitude } : null),
            ...(latitude ? { latitude } : null),
        };
    };

    const onUseStarterDataChange = (checked) => {
        setUserStarterData(checked);
        if (checked) {
            if (!Array.isArray(availableWeatherZones) || availableWeatherZones.length === 0) {
                updateAvailableWeatherZones();
            }

            if (!Array.isArray(genericAssetTemplateFolder?.templates)) {
                if (starterDataAutoFormSchemaCode) {
                    getGenericAssetTemplatesAutoForm(starterDataAutoFormSchemaCode);
                } else {
                    getGenericAssetTemplatesItemType(itemTypeId);
                }
            }
        }
    };

    const getGenericAssetTemplatesAutoForm = (schemaCode) => {
        getAutoFormGenericAssetTemplates(schemaCode).then((response) => setTemplateFolder(response.data));
    };

    const getGenericAssetTemplatesItemType = (typeId) => {
        getItemTypeGenericAssetTemplates(typeId).then((response) => setTemplateFolder(response.data));
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_item_creation_title" values={{ type: typeLabel }} />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik
                    enableReinitialize
                    initialValues={initialFormValues}
                    onSubmit={onFormSubmit}
                    validate={validate}
                >
                    {({ isSubmitting, errors, touched }) => (
                        <Form>
                            <ModalBody pb={10} borderBottom="1px" borderColor="border-secondary">
                                <InputFormikField
                                    name="itemName"
                                    type="text"
                                    label={formLabels.itemName}
                                    validate={validateItemName}
                                    isRequired
                                    isFastField
                                />

                                <SimpleGrid mt={4} columns={{ base: 1, md: 3 }} spacingX={6} spacingY={4}>
                                    <ComboboxFormikField
                                        id="bookId"
                                        name="bookId"
                                        label={formLabels.book}
                                        isRequired
                                        validate={isEmptyBook}
                                        options={books}
                                        valueKey="id"
                                        labelKey="name"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.emptyPlaceholder}
                                        isFastField
                                    />
                                    <InputFormikField
                                        name="latitude"
                                        label={formLabels.latitude}
                                        type="number"
                                        step="any"
                                        isFastField
                                    />
                                    <InputFormikField
                                        name="longitude"
                                        label={formLabels.longitude}
                                        type="number"
                                        step="any"
                                        isFastField
                                    />
                                </SimpleGrid>

                                <SimpleGrid mt={4} columns={{ base: 1, md: 2 }} spacingX={6} spacingY={4}>
                                    <ComboboxFormikField
                                        id="pricingPointId"
                                        name="pricingPointId"
                                        label={formLabels.pricingPoint}
                                        info={<FormattedMessage id="common_pricing_point_empty_tooltip" />}
                                        options={pricingPoints}
                                        labelKey="description"
                                        valueKey="id"
                                        showPlaceholder={true}
                                        placeholderValue={null}
                                        isFastField
                                    />
                                    <ComboboxFormikField
                                        id="rtelecBasisPriceId"
                                        name="rtelecBasisPriceId"
                                        label={formLabels.rtelecBasis}
                                        options={rtelecBasis}
                                        labelKey="description"
                                        valueKey="id"
                                        showPlaceholder={true}
                                        placeholderValue={null}
                                        isFastField
                                    />

                                    {extraFields}

                                    {!hideStarterDataOption && (
                                        <CheckboxFormikField
                                            id="useStarterData"
                                            name="useStarterData"
                                            onChange={(e) => onUseStarterDataChange(e.target.checked)}
                                        >
                                            <FormattedMessage id="add_item_use_starter_data_checkbox" />
                                        </CheckboxFormikField>
                                    )}
                                    {useStarterDataFlag &&
                                        ((Array.isArray(genericAssetTemplateFolder?.templates) && (
                                            <>
                                                <ComboboxFormikField
                                                    id="selectedTemplate"
                                                    name="selectedTemplate"
                                                    label={
                                                        <FormattedMessage id="add_item_starter_data_template_dropdown" />
                                                    }
                                                    options={genericAssetTemplateFolder?.templates.flatMap((x) =>
                                                        x?.fileName?.replace(removeFileExtensionRegex, '')
                                                    )}
                                                    showPlaceholder={true}
                                                    placeholderValue=""
                                                    isFastField
                                                    isRequired={useStarterDataFlag}
                                                />
                                                {isRescalingSupported && (
                                                    <InputFormikField
                                                        id="scalingValue"
                                                        name="scalingValue"
                                                        type="number"
                                                        label={<FormattedMessage id="rescaling_input_label" />}
                                                        info={<FormattedMessage id="rescaling_input_tooltip" />}
                                                        isDisabled={!isRescalingSupported}
                                                        isRequired={false}
                                                        isFastField
                                                        validate={validateScalingValue}
                                                        step="any"
                                                    ></InputFormikField>
                                                )}
                                                {displayGenericAssetWeatherZones && (
                                                    <>
                                                        <SimpleGrid
                                                            pb={0}
                                                            columns={{ xl: 1 }}
                                                            spacingX={6}
                                                            spacingY={4}
                                                        >
                                                            <ComboboxFormikField
                                                                id="weatherZoneId"
                                                                name="weatherZoneId"
                                                                label={
                                                                    <FormattedMessage id="renewable_definitions_weather_zone" />
                                                                }
                                                                isRequired={displayGenericAssetWeatherZones}
                                                                options={availableWeatherZones}
                                                                labelKey="name"
                                                                valueKey="id"
                                                                placeholderValue=""
                                                                placeholderLabel={formLabels.selectPlaceholder}
                                                                width="100%"
                                                                isDisabled={!displayGenericAssetWeatherZones}
                                                            />
                                                            <Box
                                                                label={
                                                                    <FormattedMessage id="item_creation_wizard_weatherZone_heading" />
                                                                }
                                                            >
                                                                <Text size="xs" letterSpacing="0.5px" mb={1}>
                                                                    <FormattedMessage id="item_creation_wizard_weatherZone_heading" />
                                                                </Text>
                                                                <SecondaryButton
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    onClick={createWeatherZoneModal.onOpen}
                                                                    width="50%"
                                                                    leftIcon={<AddIcon />}
                                                                >
                                                                    <Box as="span" textTransform="capitalize">
                                                                        <FormattedMessage id="common_add_new" />
                                                                    </Box>
                                                                </SecondaryButton>
                                                            </Box>
                                                        </SimpleGrid>
                                                    </>
                                                )}
                                            </>
                                        )) || <Skeleton />)}
                                </SimpleGrid>
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        isDisabled={isSubmitDisabled({ errors, touched })}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="common_item_create_btn" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
                {createWeatherZoneModal.isOpen && (
                    <AddWeatherZoneModal
                        isOpen
                        weatherZones={availableWeatherZones}
                        onClose={createWeatherZoneModal.onClose}
                    />
                )}
            </ModalContent>
        </Modal>
    );
};

export default AddItemModal;
